import React, { useState, useRef, useCallback } from 'react';
import { uploadImage } from '../../services/upload.service';
import ImageLinkInput from './ImageLinkInput';

/**
 * Real drag-and-drop (and paste, and click-to-browse) image upload —
 * no "get a share link first" detour. Jenn, 2026-08-27: pasting a
 * screenshot into a To-Do had no way in except a URL field, and dragging
 * one in silently did nothing.
 */
export default function ImageDropZone({ onUploaded, currentUrl, onClear }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = useCallback(async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('That\'s not an image file.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadImage(file);
      onUploaded(url);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not upload that image.');
    } finally {
      setUploading(false);
    }
  }, [onUploaded]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const handlePaste = (e) => {
    const item = [...(e.clipboardData?.items || [])].find(i => i.type.startsWith('image/'));
    if (item) {
      e.preventDefault();
      handleFile(item.getAsFile());
    }
  };

  if (currentUrl) {
    return (
      <div className="flex items-center gap-2">
        <img src={currentUrl} alt="" className="w-14 h-14 object-cover rounded-lg border border-gray-200" />
        <button type="button" onClick={onClear} className="text-xs text-gray-500 hover:text-red-600">Remove image</button>
      </div>
    );
  }

  return (
    <div>
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onPaste={handlePaste}
      onClick={() => fileInputRef.current?.click()}
      tabIndex={0}
      className={`border-2 border-dashed rounded-lg px-3 py-4 text-center text-xs cursor-pointer transition-colors
        ${dragging ? 'border-reset-terracotta bg-reset-terracotta/5' : 'border-gray-200 hover:border-gray-300'}`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {uploading ? (
        <span className="text-gray-400">Uploading…</span>
      ) : (
        <span className="text-gray-400">Drag an image here, paste one, or click to browse</span>
      )}
      {error && <p className="text-red-600 mt-1">{error}</p>}
    </div>
    <ImageLinkInput onLink={onUploaded} disabled={uploading} />
    </div>
  );
}
